import {
	FolderOpen,
	LayoutGrid,
	type LucideIcon,
	PenLine,
	Share2,
} from "lucide-react";
import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
	isCabinetUiEnabled,
	isWorkflowsEnabled,
} from "../utils/featureFlags";

interface NavigationItem {
	/**
	 * Either an SVG asset path (the original entries) or a lucide component (new
	 * entries — lucide is the repo's icon library, and adding a one-off SVG for
	 * an icon it already ships would be the odd choice, not this union).
	 */
	icon: string | LucideIcon;
	label: string;
	path: string;
}

interface NavigationSectionProps {
	isOpen: boolean;
}

const NavigationSection: React.FC<NavigationSectionProps> = ({ isOpen }) => {
	const location = useLocation();

	// Three entries, not four (DOP-478). "Workflow" and "Templates" were the
	// two halves of one object — where you read the numbers, and where you set
	// the extraction up — and the user had to know which half they wanted
	// before they could navigate. A workflow is the whole thing, and creating
	// one is a modal over the list of them. /kpi, /workflows, /workflow and
	// /templates all still redirect.
	//
	// The Workflows entry answers to one flag (isWorkflowsEnabled, DOP-483): a
	// tenant with no use for extraction gets neither the list nor the place
	// to create one — the whole section is absent, not empty.
	const workflowsEnabled = isWorkflowsEnabled();
	const navigationItems: NavigationItem[] = [
		...(workflowsEnabled
			? [{ icon: LayoutGrid, label: "Workflows", path: "/workflows" }]
			: []),
		// Uploading a file and connecting a system are the same job — getting
		// data into Doppler — so they share one entry: the Deal Room. /upload is
		// only the first-run page ("Let's get started" while there is no deal);
		// once there is a deal it hands over to /files, which stays under this
		// same entry.
		{ icon: FolderOpen, label: "Deal Room", path: "/upload" },
		{ icon: PenLine, label: "New Chat", path: "/chat" },
	];

	// The catalog over the ingested deal rooms, entered as the graph an answer
	// is reasoned over — "Context graph" names what the user reads there, where
	// "Cabinet" named our storage metaphor. `/cabinet` is still routed (it is
	// the same catalog, with the lint and version detail this view leaves out),
	// it just no longer needs a permanent seat in the sidebar.
	if (isCabinetUiEnabled()) {
		navigationItems.push({
			icon: Share2,
			label: "Context Graph",
			path: "/context-graph",
		});
	}

	// "New Chat" is the only entry point. Which harness it lands on is the
	// backend's call, from CLAUDE_CODE_HARNESS_ENABLED at request time — the UI
	// deliberately does not know, so flipping the arm needs no frontend change.
	// A pinned harness is still reachable via /chat?type=claude_code|agentic.

	return (
		<div className="px-4 py-2">
			<div className="space-y-1">
				{navigationItems.map((item) => {
					// An answer's neighbourhood (`/context-graph?chat=…`) is a
					// detour off a conversation, not the sidebar destination the
					// bare route is. Highlighting the tab there told the reader
					// they had left the chat, when the chat is where the Back
					// link returns them.
					// /workflows matches on PREFIX (and the /upload entry on /files/…): a workflow's own page is
					// still the Workflows section, and an exact match would unlight the
					// entry the moment the user opened one of the things it lists.
					// Everything else stays exact — /chat is "start a new one", and
					// highlighting it while reading an existing chat would say the
					// wrong thing.
					const isActive =
						(item.path === "/workflows"
							? location.pathname === item.path ||
								location.pathname.startsWith(`${item.path}/`)
							: location.pathname === item.path ||
								(item.path === "/upload" &&
									(location.pathname === "/files" ||
										location.pathname.startsWith("/files/")))) &&
						!(
							item.path === "/context-graph" &&
							new URLSearchParams(location.search).has("chat")
						);
					return (
						<Link
							key={item.path}
							to={item.path}
							// px-3, not px-1: at px-1 the pill's left edge sat on the
							// wrapper's own padding and read as flush against the
							// sidebar edge, and the icon sat 8px left of the collapsed
							// rail's centre. 16px (wrapper px-4) + 12px + half of the
							// 16px icon = 36px = half of the 72px collapsed rail, so
							// this centres it exactly.
							// The design canvas's treatment (DOP-614): line icons in the
							// text colour, and the current section as a teal TINT with
							// teal text rather than a solid fill, so it reads as "you are
							// here" without outweighing the page.
							className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors whitespace-nowrap ${
								isActive
									? "bg-[#E6F3F3] text-[#004C4B] font-medium"
									: "hover:bg-black/[0.04] text-text-nav"
							}`}
						>
							{typeof item.icon === "string" ? (
								<img
									src={item.icon}
									alt={item.label}
									className="w-4 h-4 flex-shrink-0"
								/>
							) : (
								<item.icon className="w-4 h-4 flex-shrink-0" aria-hidden />
							)}
							<span
								className={`text-[14px] transition-opacity duration-200 ${isOpen ? "opacity-100" : "opacity-0"}`}
								style={{ lineHeight: "130%" }}
							>
								{item.label}
							</span>
						</Link>
					);
				})}
			</div>
		</div>
	);
};

export default NavigationSection;
